import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { ScrollReveal } from '../components/ScrollReveal';
import './ProjectsPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const projectId = searchParams.get('projeto');
  
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(null);
  const [pageContent, setPageContent] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [projectsRes, contentRes] = await Promise.all([
          axios.get(`${API}/cms/public/projects`),
          axios.get(`${API}/cms/public/content/projetos`)
        ]);
        
        setProjects(projectsRes.data);
        
        // Extract hero content
        const heroContent = contentRes.data.find(c => c.section === 'hero');
        setPageContent(heroContent?.content || {});
        
        // If we have a projectId, find that specific project
        if (projectId) {
          const selectedProject = projectsRes.data.find(p => p.slug === projectId);
          setProject(selectedProject || null);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
      setLoading(false);
    };
    
    loadData();
  }, [projectId]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [projectId]);

  if (loading) {
    return (
      <div className="projects-page">
        <Header />
        <div style={{ padding: '200px 20px', textAlign: 'center', color: '#666' }}>
          A carregar...
        </div>
        <Footer />
      </div>
    );
  }

  // Grid view - list of all projects
  if (!project) {
    return (
      <div className="projects-page" data-testid="projects-page">
        <SEO 
          title="Projetos"
          description="Conheça os projetos da AASPS - iniciativas com impacto social nas áreas da inclusão, saúde mental, cidadania e desenvolvimento comunitário."
          keywords="projetos, AASPS, inclusão social, saúde mental, cidadania, desenvolvimento comunitário"
          url="/projetos"
        />
        <Header />
        <Breadcrumbs />
        
        <div className="projects-hero">
          <div className="projects-hero-content">
            <h1>{pageContent.title || 'Os Nossos Projetos'}</h1>
            <p>{pageContent.description || 'Projetos com impacto social nas áreas da inclusão, saúde mental, cidadania e desenvolvimento comunitário.'}</p>
          </div>
        </div>

        <section className="projects-grid-section">
          <div className="projects-grid-container">
            <h2>Projetos em Curso</h2>
            <div className="projects-grid">
              {projects.map((proj) => (
                <div 
                  key={proj.slug} 
                  className="project-card"
                  onClick={() => navigate(`/projetos?projeto=${proj.slug}`)}
                  data-testid={`card-${proj.slug}`}
                >
                  <img 
                    src={proj.image} 
                    alt={proj.title}
                    className="project-card-image"
                  />
                  <div className="project-card-content">
                    <h3>{proj.title}</h3>
                    <p>{proj.hero_description?.substring(0, 80)}...</p>
                    <span className="read-more">Saber mais →</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="cta-section">
          <div className="cta-container">
            <h2>Quer Apoiar os Nossos Projetos?</h2>
            <p>O seu contributo faz a diferença na vida de muitas pessoas.</p>
            <button className="cta-button" onClick={() => navigate('/contactos')}>
              FALE CONNOSCO
            </button>
          </div>
        </section>

        <Footer />
      </div>
    );
  }

  // Detail view - single project
  return (
    <div className="projects-page" data-testid="project-detail-page">
      <SEO 
        title={project?.title || 'Projeto'}
        description={project?.description?.substring(0, 160) || 'Detalhes do projeto da AASPS'}
        url={`/projetos?projeto=${projectId}`}
      />
      <Header />
      <Breadcrumbs customTitle={project?.title} />
      
      <div className="back-button-container">
        <div className="back-button-wrapper">
          <button className="back-button" onClick={() => navigate('/projetos')} data-testid="back-button">
            <ArrowLeft size={18} />
            Voltar aos Projetos
          </button>
        </div>
      </div>

      {/* Hero Image + Content Section */}
      <section className="media-content-section bg-white">
        <div className="media-content-container">
          <div className="media-content-image">
            <img src={project.image} alt={project.title} />
          </div>
          <div className="media-content-text">
            <h2>Sobre o Projeto</h2>
            <p>{project.full_content}</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="project-cta-section">
        <div className="cta-container">
          <h2>Quer Saber Mais?</h2>
          <p>Entre em contacto para conhecer melhor este projeto.</p>
          <button className="cta-button" onClick={() => navigate('/contactos')}>
            Contacte-nos
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProjectsPage;
